import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { t } from "i18next";
import { useMultiModalController } from "@/store/multiModalController.store";
import { useForm } from "react-hook-form";
import { AccountSettingsField } from "@/common/enums/accountSettings";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth.store";
import {
  EmailChangeSchema,
  type EmailChangeSchemaType,
} from "@/schemas/emailChange.schema";
import { EmailChangeForm } from "./EmailChangeForm";
import { useUpdateAuthUser } from "@/hooks/user/useUpdateAuthUser";
import { useNavigate } from "@tanstack/react-router";

export const EmailChangeDialog = (): JSX.Element => {
  const { userData, signOut: signOutAuth } = useAuthStore.getState();
  const { modals, setOpenByModalId } = useMultiModalController();
  const { changeEmail, loading, result } = useUpdateAuthUser();
  const navigate = useNavigate();

  const form = useForm<EmailChangeSchemaType>({
    resolver: zodResolver(EmailChangeSchema()),
    mode: "onSubmit",
    shouldUnregister: false,
    reValidateMode: "onSubmit",
    defaultValues: {
      confirmNewEmail: "",
      newEmail: "",
    },
  });

  const handleSubmit = (data: EmailChangeSchemaType): void => {
    if (userData) void changeEmail(userData.id, data.newEmail);
  };

  const signOut = async (): Promise<void> => {
    await signOutAuth();
    void navigate({ to: "/" });
  };

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("settings.account.updatedToast.successful.title"),
          description: t(
            "settings.account.updatedToast.successful.description"
          ),
        });
        setOpenByModalId(AccountSettingsField.CHANGE_EMAIL, false);
        void signOut();
      } else {
        toast({
          variant: "destructive",
          title: t("settings.account.updatedToast.error.title"),
          description: t("settings.account.updatedToast.error.description"),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading]);

  return (
    <Dialog
      open={modals[AccountSettingsField.CHANGE_EMAIL]}
      onOpenChange={(open) => {
        setOpenByModalId(AccountSettingsField.CHANGE_EMAIL, open);
      }}
    >
      <DialogContent>
        <DialogHeader className="space-y-4">
          <DialogTitle className="text-center">
            {t("settings.account.changeEmailTitle")}
          </DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <EmailChangeForm
          form={form}
          isSubmitting={loading}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
};
